import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, ReceiptLong, RemoveCircleOutline, Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { createFilterOptions, Util } from '../../util/Util';
import { excludeExpensePayModes, MYSQL_LIMITS } from '../../util/constants';
import { MONEY_LIMITS, parseMoneyInput, addMoney } from "../../util/quantity";
import RefreshButton from '../../components/RefreshButton';
import {
  MoleCancelButton,
  MoleDialog,
  MoleDialogActions,
  MoleDialogHeader,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { Z_INDEX } from '../../styles/zIndex';
import type { ExpenseCategoryOption, ExpenseDepotOption, ExpensePayeeOption, ExpenseRow } from './expenseTypes';
import {
  MAX_EXPENSE_LINES,
  SYSTEM_EXPENSE_CATEGORIES,
  expenseCategoryLabel,
  isSystemExpenseCategory,
} from './expenseTypes';
import { businessTodayDate } from "../../util/businessClock";

type PayModeOption = { id: number | string; mode_name: string };

type ExpenseLineDraft = {
  key: string;
  id?: number;
  amount: number;
  category: string;
  categoryInput: string;
  payee: string;
};

type Props = {
  isOpen: boolean;
  expense?: ExpenseRow | null;
  batchMode?: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const parseExpenseDate = (value?: string) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return new Date();
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

let lineSeq = 0;
const nextLineKey = () => `line-${++lineSeq}-${Date.now()}`;

const blankDraft = (label: (value: string) => string): ExpenseLineDraft => ({
  key: nextLineKey(),
  amount: 0,
  category: 'rent',
  categoryInput: label('rent'),
  payee: '',
});

const ExpenseModal = ({ isOpen, expense = null, batchMode = false, onClose, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { api, access } = useAuth();
  const { setLoadingMessage, queueReRun } = useUI();
  const { appSettings } = useAppSocket();
  const themeColor = useMoleThemeColor();
  const currency = appSettings?.BIZ_CURRENCY?.value || 'USD';
  const [expDate, setExpDate] = useState<Date>(() => businessTodayDate());
  const [mode, setMode] = useState<PayModeOption | null>(null);
  const [modes, setModes] = useState<PayModeOption[]>([]);
  const [depot, setDepot] = useState<ExpenseDepotOption | null>(null);
  const [depots, setDepots] = useState<ExpenseDepotOption[]>([]);
  const [categories, setCategories] = useState<ExpenseCategoryOption[]>([]);
  const [payees, setPayees] = useState<ExpensePayeeOption[]>([]);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<ExpenseLineDraft[]>([]);
  const [draft, setDraft] = useState<ExpenseLineDraft>(() => blankDraft((value) => value));
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [loadingModes, setLoadingModes] = useState(false);
  const [loadingDepots, setLoadingDepots] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPayees, setLoadingPayees] = useState(false);

  const categoryLabel = (value: string) => expenseCategoryLabel(value, t);

  const categoryChoices = useMemo(() => {
    const history = categories.map((row) => row.category);
    return [...new Set([...SYSTEM_EXPENSE_CATEGORIES, ...history])];
  }, [categories]);

  const payeeNames = useMemo(() => payees.map((row) => row.name), [payees]);
  const payeeByName = useMemo(
    () => new Map(payees.map((row) => [row.name.toLowerCase(), row])),
    [payees],
  );

  const resolveCategory = (typed: string) => {
    const value = typed.trim();
    if (!value) return '';
    const key = value.toLowerCase();
    if (isSystemExpenseCategory(key)) return key;
    const byLabel = SYSTEM_EXPENSE_CATEGORIES.find(
      (option) => categoryLabel(option).toLowerCase() === key,
    );
    return byLabel ?? value;
  };

  const emptyLine = (row?: ExpenseRow): ExpenseLineDraft => ({
    key: row?.id ? `line-${row.id}` : nextLineKey(),
    id: row?.id,
    amount: Number(row?.amount || 0),
    category: row?.category || 'rent',
    categoryInput: categoryLabel(row?.category || 'rent'),
    payee: row?.payee || '',
  });

  const isLineForm = !expense || batchMode;
  const isBatchEdit = Boolean(batchMode && expense);
  const canAddLines = isLineForm;

  const payeeSourceLabel = (sources: string[] = []) =>
    sources
      .map((source) => t(`common:expense.sources.${source}`))
      .filter((label) => !label.startsWith('common:'))
      .join(', ');

  const loadModes = async () => {
    setLoadingModes(true);
    const res = await api.get('/payment/modes/active');
    if (res?.status === 200) {
      const nextModes = excludeExpensePayModes((res.data || []) as PayModeOption[]);
      setModes(nextModes);
      setMode((current) => {
        if (current) {
          return nextModes.find((row) => String(row.id) === String(current.id)) || current;
        }
        if (expense) {
          return nextModes.find((row) => Number(row.id) === Number(expense.mode)) || nextModes[0] || null;
        }
        return nextModes[0] || null;
      });
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(loadModes);
    }
    setLoadingModes(false);
  };

  const loadDepots = async () => {
    setLoadingDepots(true);
    const res = await api.get('/expenses/depots');
    if (res?.status === 200) {
      const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setDepots(rows);
      setDepot((current) => {
        const wanted = current?.id ?? expense?.depot;
        if (!wanted) return current;
        return rows.find((row: ExpenseDepotOption) => Number(row.id) === Number(wanted)) || current;
      });
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(loadDepots);
    }
    setLoadingDepots(false);
  };

  const loadCategories = async () => {
    setLoadingCategories(true);
    const res = await api.get('/expenses/categories');
    if (res?.status === 200 && Array.isArray(res.data)) {
      setCategories(res.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(loadCategories);
    }
    setLoadingCategories(false);
  };

  const loadPayees = async () => {
    setLoadingPayees(true);
    const res = await api.get('/expenses/payees');
    if (res?.status === 200 && Array.isArray(res.data)) {
      setPayees(res.data);
    } else if (res?.status === 401 || res?.status === 422) {
      queueReRun(loadPayees);
    }
    setLoadingPayees(false);
  };

  const loadLookups = () => Promise.all([loadModes(), loadDepots(), loadCategories(), loadPayees()]);

  const fieldLabel = (text: string, loading: boolean, onRefresh: () => void) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {text}
      <RefreshButton isWorking={loading} onClick={onRefresh} disabled={loading} />
    </Box>
  );

  useEffect(() => {
    if (!isOpen) return;
    const hydrate = async () => {
      setEditingKey(null);
      setDraft(blankDraft(categoryLabel));
      if (expense && batchMode && expense.batch) {
        setDescription(expense.description || '');
        setExpDate(parseExpenseDate(expense.date));
        setDepot(
          expense.depot
            ? { id: expense.depot, depot_name: expense.depot_name || '' }
            : null,
        );
        setMode(null);
        const res = await api.get(`/expenses/batch/${encodeURIComponent(expense.batch)}`);
        const rows: ExpenseRow[] = Array.isArray(res?.data) ? res.data : [];
        const first = rows[0] || expense;
        setExpDate(parseExpenseDate(first.date));
        setDepot(
          first.depot
            ? { id: first.depot, depot_name: first.depot_name || '' }
            : null,
        );
        setDescription(first.description || '');
        setLines((rows.length ? rows : [expense]).map((row) => emptyLine(row)));
        const modeRes = await api.get('/payment/modes/active');
        const nextModes = excludeExpensePayModes((modeRes?.data || []) as PayModeOption[]);
        setMode(nextModes.find((row) => Number(row.id) === Number(first.mode)) || null);
      } else if (expense) {
        setExpDate(parseExpenseDate(expense.date));
        setDepot(
          expense.depot
            ? { id: expense.depot, depot_name: expense.depot_name || '' }
            : null,
        );
        setDescription(expense.description || '');
        const single = emptyLine(expense);
        setLines([single]);
        setDraft({ ...single, categoryInput: categoryLabel(single.category) });
        setEditingKey(single.key);
        setMode(null);
      } else {
        setExpDate(new Date());
        setDepot(null);
        setDescription('');
        setLines([]);
      }
      void loadLookups();
    };
    void hydrate();
  }, [isOpen, expense, batchMode]);

  const draftIsValid =
    draft.amount > 0 && draft.category.trim().length > 0 && draft.payee.trim().length > 0;

  const addOrUpdateLine = () => {
    if (!draftIsValid) return;
    const next: ExpenseLineDraft = {
      ...draft,
      key: editingKey || draft.key || nextLineKey(),
      category: resolveCategory(draft.category),
      payee: draft.payee.trim(),
    };
    if (editingKey) {
      setLines((current) => current.map((line) => (line.key === editingKey ? { ...line, ...next } : line)));
      setEditingKey(null);
    } else {
      if (lines.length >= MAX_EXPENSE_LINES) return;
      setLines((current) => [...current, next]);
    }
    setDraft(blankDraft(categoryLabel));
  };

  const loadLineIntoDraft = (line: ExpenseLineDraft) => {
    setDraft({
      ...line,
      categoryInput: categoryLabel(line.category),
    });
    setEditingKey(line.key);
  };

  const removeLine = (key: string) => {
    setLines((current) => current.filter((line) => line.key !== key));
    if (editingKey === key) {
      setEditingKey(null);
      setDraft(blankDraft(categoryLabel));
    }
  };

  const committedLines = (): ExpenseLineDraft[] => {
    if (!draftIsValid) return lines;
    const next: ExpenseLineDraft = {
      ...draft,
      key: editingKey || draft.key || nextLineKey(),
      category: resolveCategory(draft.category),
      payee: draft.payee.trim(),
    };
    if (editingKey) {
      return lines.map((line) => (line.key === editingKey ? { ...line, ...next } : line));
    }
    if (canAddLines && !lines.some((line) => line.key === next.key)) {
      return [...lines, next];
    }
    if (!canAddLines && lines.length === 1) {
      return [{ ...lines[0], ...next }];
    }
    return lines;
  };

  const saveLines = committedLines();
  const lineTotal = saveLines.reduce((sum, line) => addMoney(sum, Number(line.amount || 0)), 0);
  const canSubmit =
    !!mode
    && saveLines.length > 0
    && saveLines.length <= MAX_EXPENSE_LINES
    && saveLines.every((line) => line.amount > 0 && line.category.trim().length > 0 && line.payee.trim().length > 0)
    && (isLineForm || saveLines.length === 1);

  const handleSave = async () => {
    if (!canSubmit || !mode) return;
    if (!Util.checkDating(Util.formatISODate(expDate), access, 'expense', expense ? 'update' : 'create')) return;
    const shared = {
      date: Util.formatISODate(expDate),
      mode: Number(mode.id),
      depot: depot?.id ?? null,
      description: description.trim() || null,
    };
    const linePayload = {
      ...shared,
      lines: saveLines.map((line) => ({
        id: line.id,
        amount: line.amount,
        category: resolveCategory(line.category),
        payee: line.payee.trim(),
      })),
    };
    const first = saveLines[0];
    const payload = isLineForm
      ? linePayload
      : {
          ...shared,
          amount: first.amount,
          category: resolveCategory(first.category),
          payee: first.payee.trim(),
        };
    setLoadingMessage(expense ? 'UPDATE' : 'SAVE');
    try {
      const res = isBatchEdit && expense?.batch
        ? await api.put(`/expenses/batch/${encodeURIComponent(expense.batch)}`, payload)
        : expense
          ? await api.put(`/expenses/${expense.id}`, payload)
          : await api.post('/expenses', payload);
      if (res?.status === 201 || res?.status === 204 || res?.status === 200) {
        toast.success(
          t(expense ? 'common:toast.success.expenseUpdate' : 'common:toast.success.expense'),
          { autoClose: 3000 },
        );
        onSuccess();
        onClose();
      }
    } finally {
      setLoadingMessage(null);
    }
  };

  const renderCategoryField = (
    value: string,
    onValue: (next: string) => void,
  ) => (
    <Autocomplete
      freeSolo
      openOnFocus
      autoHighlight
      options={categoryChoices}
      getOptionLabel={(option) => categoryLabel(String(option))}
      filterOptions={createFilterOptions((opt) => `${String(opt)} ${categoryLabel(String(opt))}`)}
      groupBy={(option) =>
        isSystemExpenseCategory(String(option))
          ? t('common:expense.systemPresets')
          : t('common:expense.historyPresets')
      }
      value={value}
      onChange={(_, next) => {
        const selected = typeof next === 'string' ? next : '';
        onValue(resolveCategory(selected));
      }}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset' || reason === 'clear') return;
        onValue(next);
      }}
      loading={loadingCategories}
      slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
      renderOption={(props, option) => (
        <li {...props} key={String(option)}>
          {categoryLabel(String(option))}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          size="small"
          required
          label={fieldLabel(t('common:expense.category'), loadingCategories, loadCategories)}
        />
      )}
    />
  );

  const renderPayeeField = (value: string, onValue: (next: string) => void) => (
    <Autocomplete
      freeSolo
      openOnFocus
      autoHighlight
      options={payeeNames}
      getOptionLabel={(option) => String(option)}
      filterOptions={createFilterOptions((option) => String(option))}
      value={value}
      onChange={(_, next) => onValue(typeof next === 'string' ? next : '')}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset' || reason === 'clear') return;
        onValue(next);
      }}
      loading={loadingPayees}
      slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
      renderOption={(props, option) => {
        const meta = payeeByName.get(String(option).toLowerCase());
        const sourceText = payeeSourceLabel(meta?.sources);
        return (
          <li {...props} key={String(option)}>
            {option}
            {sourceText ? ` · ${sourceText}` : ''}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          size="small"
          required
          label={fieldLabel(t('common:expense.payee'), loadingPayees, loadPayees)}
        />
      )}
    />
  );

  const lineColumns: GridColDef<ExpenseLineDraft>[] = [
    {
      field: 'category',
      headerName: t('common:expense.category'),
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => categoryLabel(row.category),
    },
    {
      field: 'payee',
      headerName: t('common:expense.payee'),
      flex: 1.2,
      minWidth: 160,
    },
    {
      field: 'amount',
      headerName: t('common:tables.amount'),
      type: 'number',
      width: 140,
      renderCell: (params) => Util.formatNumber(Number(params.row.amount || 0)),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 72,
      renderCell: (params: GridRenderCellParams<ExpenseLineDraft>) => (
        <Tooltip title={t('common:ui.tooltips.delete')}>
          <span>
            <IconButton
              color="error"
              size="small"
              disabled={!canAddLines && lines.length <= 1}
              onClick={(event) => {
                event.stopPropagation();
                removeLine(params.row.key);
              }}
            >
              <RemoveCircleOutline />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <MoleDialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      themeColor={themeColor}
      paperOptions={{ maxWidth: '95%', width: '95%', maxHeight: '95vh' }}
      disableEnforceFocus
    >
      <MoleDialogHeader
        title={t(isBatchEdit ? 'common:expense.editBatchTitle' : expense ? 'common:expense.editTitle' : 'common:expense.postTitle')}
        subtitle={`${t('common:report.grandTotal')}: ${Util.formatCurrency(lineTotal, currency)}`}
        icon={<ReceiptLong />}
        onClose={onClose}
        themeColor={themeColor}
      />
      <DialogContent>
        <Paper elevation={24} sx={{ p: 1, mb: 1 }}>
          <Grid container spacing={1} alignItems="flex-start">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {t('common:tables.date')}
                      <RefreshButton isWorking={false} onClick={() => setExpDate(new Date())} />
                    </Box>
                  }
                  value={expDate}
                  onChange={(next) => {
                    if (!next) return;
                    const nextDate = next instanceof Date ? next : next.toDate();
                    if (Number.isNaN(nextDate.getTime())) return;
                    setExpDate(nextDate);
                  }}
                  slotProps={{
                    textField: { fullWidth: true, variant: 'filled', size: 'small' },
                    popper: { sx: { zIndex: Z_INDEX.dialogPopover } },
                    dialog: { sx: { zIndex: Z_INDEX.dialogPopover } },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                openOnFocus
                autoHighlight
                options={modes}
                getOptionLabel={(row) => row.mode_name || ''}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                value={mode}
                onChange={(_, row) => setMode(row)}
                loading={loadingModes}
                slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    size="small"
                    required
                    label={fieldLabel(t('common:tables.mode'), loadingModes, loadModes)}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                openOnFocus
                autoHighlight
                options={depots}
                getOptionLabel={(row) => row.depot_name || row.name || ''}
                isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                value={depot}
                onChange={(_, row) => setDepot(row)}
                loading={loadingDepots}
                slotProps={{ popper: { sx: { zIndex: Z_INDEX.dialogPopover } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    size="small"
                    label={fieldLabel(t('common:tables.depot'), loadingDepots, loadDepots)}
                    helperText={t('common:expense.depotHint')}
                  />
                )}
              />
            </Grid>
            {expense ? (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  variant="filled"
                  size="small"
                  fullWidth
                  label={t('common:expense.batch')}
                  value={expense.batch || ''}
                  disabled
                  helperText={t('common:expense.batchHint')}
                />
              </Grid>
            ) : null}
          </Grid>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={1} alignItems="flex-start">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              {renderCategoryField(
                draft.category,
                (next) => setDraft((current) => ({ ...current, category: next, categoryInput: next })),
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              {renderPayeeField(draft.payee, (next) => setDraft((current) => ({ ...current, payee: next })))}
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                variant="filled"
                size="small"
                fullWidth
                type="number"
                label={t('common:tables.amount')}
                value={Number.isFinite(draft.amount) ? draft.amount : 0}
                onFocus={(e) => e.target.select()}
                inputProps={{ min: 0.01, max: MONEY_LIMITS.max, step: 0.01 }}
                onChange={(e) => {
                  const parsed = parseMoneyInput(e.target.value);
                  if (parsed === null) return;
                  setDraft((current) => ({
                    ...current,
                    amount: Math.max(0, Math.min(MONEY_LIMITS.max, parsed)),
                  }));
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }} display="flex" alignItems="flex-start">
              <Button
                fullWidth
                size="large"
                color="success"
                startIcon={editingKey ? <Save /> : <Add />}
                disabled={!draftIsValid || (!editingKey && !canAddLines)}
                onClick={addOrUpdateLine}
                sx={{ fontWeight: 'bolder' }}
              >
                {t(editingKey ? 'common:expense.updateLine' : 'common:expense.addLine')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
        <Paper elevation={2} sx={{ height: 320 }}>
          <DataGrid
            rows={lines}
            columns={lineColumns}
            getRowId={(row) => row.key}
            disableRowSelectionOnClick
            onRowClick={(params) => loadLineIntoDraft(params.row)}
            hideFooter={lines.length < 25}
            localeText={{
              noRowsLabel: t('common:expense.tableEmpty'),
            }}
            sx={{ border: 0 }}
          />
        </Paper>
      </DialogContent>
      <MoleDialogActions sx={{ justifyContent: 'stretch' }}>
        <Grid container spacing={1} alignItems="center" width="100%">
          <Grid sx={{ flexGrow: 1, minWidth: 0 }}>
            <TextField
              size="small"
              label={t('common:ui.labels.notes')}
              variant="filled"
              fullWidth
              value={description}
              inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid
            sx={{
              width: { xs: '100%', md: 'auto' },
              display: 'flex',
              justifyContent: { xs: 'space-between', md: 'flex-end' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              disabled={!canSubmit}
              startIcon={<Save />}
            >
              {t(expense ? 'common:ui.buttons.save' : 'common:ui.buttons.confirm')}
            </Button>
            <MoleCancelButton onClick={onClose}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
          </Grid>
        </Grid>
      </MoleDialogActions>
    </MoleDialog>
  );
};

export default ExpenseModal;
